import {
  extractName,
  FieldMappingEditorValue,
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldMappingVariables,
  MappableVariable,
} from '@components/text-editors/addons/field-mapping';

import { FieldConfigValue } from './types';

/**
 * Rebuild the `variables` map for a stored value that carries `{{ token }}`s
 * without one.
 *
 * A field-mapping editor turns a token into a pill only when the token has an
 * entry in `variables` — `transformInput` leaves an unknown token as literal
 * text, so the recruiter reads "{{ application_name }}" where a pill belongs.
 * The config endpoints accept a bare string as a stored value (the backend's
 * validate_variables_are_dicts only inspects `variables` when `value` is a
 * dict), so a value written by anything other than the editor itself — an API
 * client, a fixture, an earlier revision of the panels — arrives tokenised but
 * mapless, and every editor downstream renders it raw.
 *
 * The token → definition mapping is recoverable: `extractName` derives a
 * token from a variable's own source/field exactly as `pillToVariable` does
 * when writing one, so scanning the available groups for tokens the value
 * mentions reconstructs the map. Unrecognised tokens are left alone — they
 * still render literally, which is the honest outcome for a variable this
 * company/job does not have.
 *
 * Pass the *unfiltered* group list: which variables a field is allowed to
 * offer is a picker concern, whereas a token already in the stored value has
 * to resolve regardless of the field's type filter.
 */
/**
 * The data-only entry a stored `variables` map holds, mirroring what
 * `pillToVariable` writes when the editor itself creates a pill: source, field,
 * label, plus action / type / format / formatLanguage where present.
 *
 * A variable-group field carries more than that — `icon` (a React element),
 * `iconRight`, `options`, `optionsFromListId`, `excludedOptions` — which are
 * picker metadata; `transformInput` destructures exactly those out before using
 * a variable. Copying a whole field object into the stored value would put a
 * React element in it, and its fiber back-references make JSON.stringify throw
 * when the send payload is serialized.
 */
const toStoredVariableEntry = (field: FieldMappingFieldItem): FieldMappingFieldItem => {
  const entry = {
    source: field.source,
    field: field.field,
    label: field.label,
  } as FieldMappingFieldItem;
  if (field.action) entry.action = field.action;
  if (field.type) entry.type = field.type;
  if (field.format) entry.format = field.format;
  if (field.formatLanguage) entry.formatLanguage = field.formatLanguage;
  return entry;
};

export const rehydrateVariables = (
  value: string,
  groups: FieldMappingGroup[],
  existing: FieldMappingVariables = {},
): FieldMappingEditorValue => {
  const variables: FieldMappingVariables = { ...existing };

  for (const group of groups) {
    for (const field of group.fields ?? []) {
      // extractName's parameter union narrows `source` to 'action' for the
      // action case; a group field types it as a plain string, and the two
      // describe the same data. Deriving the token here instead would fork the
      // naming rule away from pillToVariable, which is what writes the tokens.
      const token = extractName(field as FieldMappingFieldItem as MappableVariable);
      // An entry the value already carries wins: it may describe a variable
      // this company no longer offers, and re-deriving would drop it.
      if (!token || variables[token]) continue;
      // Tolerate any inner spacing: {{token}} and {{ token }} are both written
      // in practice, and normalizeVariables accepts either on the way in.
      if (new RegExp(`{{\\s*${escapeForRegExp(token)}\\s*}}`).test(value)) {
        variables[token] = toStoredVariableEntry(field);
      }
    }
  }

  return { value, variables };
};

/**
 * Coerce a stored value into what an editor can render: a dict passes through
 * (it already carries its own variables), and a string is tokenised-and-mapped
 * by rehydrateVariables. Everything else has no editor representation.
 */
export const toMappedEditorValue = (
  value: FieldConfigValue | undefined,
  groups: FieldMappingGroup[],
): FieldMappingEditorValue | undefined => {
  if (typeof value === 'string') return rehydrateVariables(value, groups);
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined;

  // A dict needs the same repair as a bare string: serialize.ts wraps a stored
  // string as {value, variables: {}}, so a config saved after loading one holds
  // a dict that is tokenised but mapless, and passing it through untouched
  // renders the raw token exactly as the bare string did.
  const stored = value as FieldMappingEditorValue;
  if (typeof stored.value !== 'string') return stored;
  const repaired = rehydrateVariables(stored.value, groups, stored.variables ?? {});
  // Same object back when nothing was missing, so the uncontrolled editors
  // reading this value are not remounted for an identical map.
  const addedCount =
    Object.keys(repaired.variables ?? {}).length - Object.keys(stored.variables ?? {}).length;
  return addedCount === 0 ? stored : { ...stored, variables: repaired.variables };
};

const escapeForRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
